import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Collapse,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CallSplit as CallSplitIcon,
  LocalShipping as ContainerIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  MergeType as MergeIcon,
  CallSplit as UnmergeIcon,
  InsertDriveFile as FileIcon,
  Folder as FolderIcon,
  FormatListNumbered as FormatListNumberedIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { api } from '../config/api';
import ContainerDetailModal from './ContainerDetailModal';
import DailyContainerReport from './DailyContainerReport';

interface Container {
  id: number;
  container_no: string;
  seal_no: string;
  delivery_day: string;
  delivery_time: string;
  delivery_sequence: number | null;
  dock: string;
  status: 'in_transit' | 'received' | 'Completed' | 'Finalised' | 'Draft' | 'In Progress' | 'archived';
  line_count: number;
  is_priority: boolean;
  priority_comment: string;
  supplier_code: string;
  chain_of_custody: string;
  created_at: string;
  // Multi-PO fields
  is_shared: boolean;
  po_numbers: string[];
  primary_po_number: string | null;
  po_summary: Array<{
    po_number: string;
    line_count: number;
    unique_stocks: number;
  }>;
}

interface PackingListGroup {
  import_id: number;
  filename: string;
  po_number: string;
  supplier_name: string;
  uploaded_at: string;
  total_lines: number;
  lines_matched: number;
  lines_unmatched: number;
  processing_status: string;
  expected_delivery: string | null;
  containers: Container[];
  // Batch/merge fields
  effective_batch_id?: string;
  import_ids?: number[];
  filenames?: string[];
  batch_description?: string;
  is_merged?: boolean;
}

interface ContainerLine {
  id: number;
  stock_code: string;
  po_number: string | null;
  rolls_per_pallet: number;
  sheets: number;
  metres: number;
  prepopulated_qty: number;
  net_weight_tonnes: number;
  linear_length_m: number;
}

// Same pallet/reel split the container card's table uses
const isReelLine = (line: ContainerLine) =>
  line.sheets === 0 && (line.rolls_per_pallet > 0 || line.metres > 0);

// "4 pallets · 12,000 sheets" / "6 reels · 9,800 kg" for one stock code's lines
const describeStockQty = (lines: ContainerLine[]): string => {
  const reels = lines.filter(isReelLine);
  const pallets = lines.filter(l => !isReelLine(l));
  const parts: string[] = [];
  if (pallets.length > 0) {
    const sheets = pallets.reduce((sum, l) => sum + (l.sheets || 0), 0);
    parts.push(`${pallets.length} pallet${pallets.length === 1 ? '' : 's'} · ${sheets.toLocaleString()} sheets`);
  }
  if (reels.length > 0) {
    const kg = Math.round(reels.reduce((sum, l) => sum + (l.net_weight_tonnes || 0) * 1000, 0));
    parts.push(`${reels.length} reel${reels.length === 1 ? '' : 's'} · ${kg.toLocaleString()} kg`);
  }
  return parts.join(', ');
};

interface ContainerDashboardProps {
  onNavigateToPO?: (poNumber: string) => void;
  onContainerClick?: (container: Container) => void;
}

// Helper to check if container is received (handles old, new, and archived status values)
const isContainerReceived = (status: string | undefined): boolean => {
  return status === 'received' || status === 'Completed' || status === 'Finalised' || status === 'archived';
};

const ContainerDashboard: React.FC<ContainerDashboardProps> = ({
  onNavigateToPO,
  onContainerClick
}) => {
  const [packingListGroups, setPackingListGroups] = useState<PackingListGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [expandedPacking, setExpandedPacking] = useState<number | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // Search applied on blur/enter
  const [dateFilter, setDateFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  // Container card state
  const [expandedContainers, setExpandedContainers] = useState<number[]>([]);
  const [containerLines, setContainerLines] = useState<Record<number, ContainerLine[]>>({});
  // Per packing list: the stock code the user is hunting for, and whether its lines are loading
  const [stockFocus, setStockFocus] = useState<Record<number, string | null>>({});
  const [loadingShipmentLines, setLoadingShipmentLines] = useState<Record<number, boolean>>({});

  // Delete packing list state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [packingListToDelete, setPackingListToDelete] = useState<PackingListGroup | null>(null);

  // Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Merge state
  const [selectedForMerge, setSelectedForMerge] = useState<Set<number>>(new Set());
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [unmergeLoading, setUnmergeLoading] = useState(false);

  // Harders sequence state
  const [hardersModalOpen, setHardersModalOpen] = useState(false);
  const [hardersPackingList, setHardersPackingList] = useState<PackingListGroup | null>(null);
  const [hardersText, setHardersText] = useState('');
  const [hardersSubmitting, setHardersSubmitting] = useState(false);
  const [hardersResult, setHardersResult] = useState<any>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  // Load packing list groups
  const loadPackingListGroups = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters from filters
      const params = new URLSearchParams();
      if (appliedSearchTerm) params.append('search', appliedSearchTerm);
      if (dateFilter) params.append('date', dateFilter);
      if (supplierFilter) params.append('supplier', supplierFilter);

      const response = await api.get(`/containers-by-packing-list?${params}`);

      if (response.data.success) {
        setPackingListGroups(response.data.packing_lists || []);
      } else {
        setError('Failed to load packing lists');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load packing lists');
      console.error('Error loading packing lists:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackingListGroups();
  }, [appliedSearchTerm, dateFilter, supplierFilter]);

  const handleAccordionChange = (packingList: PackingListGroup) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPacking(isExpanded ? packingList.import_id : null);
    if (isExpanded) loadShipmentLines(packingList);
  };

  // One request for every container in the shipment - feeds the stock code
  // finder and Show All, instead of one request per container clicked.
  const loadShipmentLines = async (packingList: PackingListGroup) => {
    const missing = packingList.containers.map(c => c.id).filter(id => !containerLines[id]);
    if (missing.length === 0) return;

    setLoadingShipmentLines(prev => ({ ...prev, [packingList.import_id]: true }));
    try {
      const response = await api.get(`/container-lines-batch?ids=${missing.join(',')}`);
      const byContainer: Record<string, ContainerLine[]> = response.data.lines_by_container || {};
      setContainerLines(prev => {
        const next = { ...prev };
        Object.entries(byContainer).forEach(([id, lines]) => { next[Number(id)] = lines; });
        return next;
      });
    } catch (err) {
      console.error('Error loading shipment lines:', err);
    } finally {
      setLoadingShipmentLines(prev => ({ ...prev, [packingList.import_id]: false }));
    }
  };

  const setAllContainersExpanded = (packingList: PackingListGroup, expand: boolean) => {
    const ids = packingList.containers.map(c => c.id);
    setExpandedContainers(prev => expand
      ? Array.from(new Set([...prev, ...ids]))
      : prev.filter(id => !ids.includes(id)));
    if (expand) loadShipmentLines(packingList);
  };

  const getStatusColor = (status?: string): 'default' | 'primary' | 'secondary' | 'success' => {
    if (isContainerReceived(status)) return 'success';
    switch (status) {
      case 'in_transit':
      case 'Draft':
      case 'In Progress':
        return 'primary';
      default: return 'default';
    }
  };

  const getDockDisplayName = (dock: string): string => {
    const dockMap: { [key: string]: string } = {
      'DOCK_1': 'Dock 1',
      'DOCK_2': 'Dock 2',
      'DOCK_3': 'Dock 3',
      'DOCK_4': 'Dock 4',
      'DOCK_5': 'Dock 5'
    };
    return dockMap[dock] || dock;
  };

  const dockOptions = [
    { value: 'Dock 1', label: 'Dock 1' },
    { value: 'Dock 2', label: 'Dock 2' },
    { value: 'Dock 3', label: 'Dock 3' },
    { value: 'Dock 4', label: 'Dock 4' },
    { value: 'Dock 5', label: 'Dock 5' }
  ];

  // Container card handlers
  const handleContainerExpand = (containerId: number) => {
    if (expandedContainers.includes(containerId)) {
      setExpandedContainers(prev => prev.filter(id => id !== containerId));
    } else {
      setExpandedContainers(prev => [...prev, containerId]);
      loadContainerLines(containerId);
    }
  };

  const loadContainerLines = async (containerId: number) => {
    if (containerLines[containerId]) return; // Already loaded

    try {
      const response = await api.get(`/containers/${containerId}/lines`);
      setContainerLines(prev => ({
        ...prev,
        [containerId]: response.data.lines || []
      }));
    } catch (err) {
      console.error('Error loading container lines:', err);
    }
  };

  const handleUpdateContainer = async (containerId: number, field: string, value: string) => {
    try {
      // Blank input means "clear this field" — send null so Postgres nulls the column
      // instead of rejecting "" against typed (DATE/TIME) columns.
      const payloadValue = value === '' ? null : value;
      await api.put(`/containers/${containerId}`, { [field]: payloadValue });
      applyContainerLocalUpdate(containerId, field, value);
    } catch (err) {
      console.error('Error updating container:', err);
      alert('Failed to update container');
    }
  };

  // Updates parent state without hitting the API. Used by callers that have
  // already done their own PUT (e.g. inline edits that need custom error UX).
  const applyContainerLocalUpdate = (containerId: number, field: string, value: string) => {
    setPackingListGroups(prev => prev.map(pl => ({
      ...pl,
      containers: pl.containers.map(c =>
        c.id === containerId ? { ...c, [field]: value } : c
      )
    })));
  };

  const handlePriorityToggle = async (container: Container) => {
    try {
      if (container.is_priority) {
        // Remove priority
        await api.delete(`/containers/${container.id}/priority`);
      } else {
        // Would open a modal to add priority comment, but for now just alert
        alert('Priority toggle - would open modal to add comment');
      }

      // Reload data
      await loadPackingListGroups();
    } catch (err) {
      console.error('Error toggling priority:', err);
    }
  };

  // Delete packing list handlers
  const handleDeleteClick = async (packingList: PackingListGroup, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent accordion from toggling
    setPackingListToDelete(packingList);
    setDeleteLoading(true);
    setError(null);

    try {
      // Preview what will be deleted
      const response = await api.delete(
        `/packing-list-imports/${packingList.import_id}?mode=preview`
      );

      setDeletePreview(response.data);
      setDeleteConfirmOpen(true);
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.blocked_containers) {
        // Show blocked containers error
        setError(`Cannot delete: ${err.response.data.blocked_containers.length} container(s) already receipted (${err.response.data.blocked_containers.join(', ')})`);
      } else {
        setError(err.response?.data?.error || 'Failed to preview deletion');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!packingListToDelete) return;

    setDeleteLoading(true);
    setError(null);

    try {
      const response = await api.delete(
        `/packing-list-imports/${packingListToDelete.import_id}`
      );

      if (response.data.success) {
        // Close dialog and refresh
        setDeleteConfirmOpen(false);
        setPackingListToDelete(null);
        setDeletePreview(null);
        await loadPackingListGroups();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete packing list');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPackingListToDelete(null);
    setDeletePreview(null);
  };

  // Archive packing list handler
  // Harders sequence: open upload modal
  const openHardersModal = (packingList: PackingListGroup, event: React.MouseEvent) => {
    event.stopPropagation();
    setHardersPackingList(packingList);
    setHardersText('');
    setHardersResult(null);
    setHardersModalOpen(true);
  };

  const closeHardersModal = () => {
    setHardersModalOpen(false);
    setHardersPackingList(null);
    setHardersText('');
    setHardersResult(null);
  };

  const handleApplyHardersSequence = async () => {
    if (!hardersPackingList?.po_number || !hardersText.trim()) return;
    setHardersSubmitting(true);
    setHardersResult(null);
    try {
      const response = await api.post('/containers/apply-harders-sequence', {
        po_number: hardersPackingList.po_number,
        text_block: hardersText,
      });
      setHardersResult(response.data);
      if (response.data.success && response.data.updated_count > 0) {
        await loadPackingListGroups();
      }
    } catch (err: any) {
      setHardersResult({ success: false, error: err.response?.data?.error || 'Failed to apply sequence' });
    } finally {
      setHardersSubmitting(false);
    }
  };

  // Copy delivery plan in Harders-style format: "CONTAINER_NO   Thu 9/4 6 AM"
  const formatDeliveryLine = (c: Container): string | null => {
    if (!c.delivery_day) return null;
    const d = new Date(c.delivery_day + 'T00:00:00');
    const weekday = d.toLocaleDateString('en-AU', { weekday: 'short' });
    const dayMonth = `${d.getDate()}/${d.getMonth() + 1}`;
    const rawTime = c.delivery_time ? c.delivery_time.trim() : '';
    // Trim stored HH:MM:SS (or HH:MM:SS.x) down to HH:MM for a cleaner Harders paste
    const time = rawTime.match(/^\d{1,2}:\d{2}/)?.[0] ?? rawTime;
    return `${c.container_no}   ${weekday} ${dayMonth}${time ? ' ' + time : ''}`;
  };

  const handleCopyDeliveryPlan = async (packingList: PackingListGroup, event: React.MouseEvent) => {
    event.stopPropagation();
    const lines: string[] = [];
    let skipped = 0;
    for (const c of packingList.containers) {
      const line = formatDeliveryLine(c);
      if (line) lines.push(line);
      else skipped += 1;
    }
    if (lines.length === 0) {
      setCopyFeedback('No containers have delivery dates set yet.');
      return;
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyFeedback(
        skipped > 0
          ? `Copied ${lines.length} container(s). Skipped ${skipped} without a delivery date.`
          : `Copied ${lines.length} container(s) to clipboard.`
      );
    } catch {
      setCopyFeedback('Copy failed — clipboard permission denied.');
    }
  };

  // Fetch and display container report in modal
  const handleViewContainerReport = async (container: Container) => {
    try {
      setReportLoading(true);
      const response = await api.get(`/containers/${container.id}/report-data`);

      if (response.data.success) {
        setReportData(response.data.container);
        setReportModalOpen(true);
      } else {
        setError('Failed to load container report');
      }
    } catch (err: any) {
      console.error('Error loading container report:', err);
      setError(err.response?.data?.error || 'Failed to load container report');
    } finally {
      setReportLoading(false);
    }
  };

  // Merge selection handlers
  const handleMergeCheckboxChange = (importId: number, checked: boolean) => {
    setSelectedForMerge(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(importId);
      } else {
        newSet.delete(importId);
      }
      return newSet;
    });
  };

  const handleOpenMergeDialog = () => {
    setMergeDialogOpen(true);
  };

  const handleCloseMergeDialog = () => {
    setMergeDialogOpen(false);
  };

  const handleMergeConfirm = async (description: string) => {
    if (selectedForMerge.size < 2) return;

    try {
      const response = await api.post('/packing-list-batches/merge', {
        import_ids: Array.from(selectedForMerge),
        description: description.trim()
      });

      if (response.data.success) {
        // Clear selection and close dialog
        setSelectedForMerge(new Set());
        setMergeDialogOpen(false);
        // Reload data
        await loadPackingListGroups();
      }
    } catch (err: any) {
      console.error('Error merging packing lists:', err);
      setError(err.response?.data?.error || 'Failed to merge packing lists');
      throw err; // Re-throw so dialog can handle loading state
    }
  };

  const handleUnmerge = async (packingList: PackingListGroup, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!packingList.effective_batch_id) return;

    if (!window.confirm(`Unmerge this batch? The ${packingList.filenames?.length || 0} files will appear separately again.`)) {
      return;
    }

    setUnmergeLoading(true);
    setError(null);

    try {
      const response = await api.post(`/packing-list-batches/${packingList.effective_batch_id}/unmerge`);

      if (response.data.success) {
        await loadPackingListGroups();
      }
    } catch (err: any) {
      console.error('Error unmerging packing list:', err);
      setError(err.response?.data?.error || 'Failed to unmerge packing list');
    } finally {
      setUnmergeLoading(false);
    }
  };

  // Get selected packing lists for merge preview - memoized to prevent expensive recalculation on every keystroke
  const selectedPackingLists = useMemo(
    () => packingListGroups.filter(pl => selectedForMerge.has(pl.import_id)),
    [packingListGroups, selectedForMerge]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Get unique suppliers for filter dropdown
  const uniqueSuppliers = Array.from(
    new Set(packingListGroups.map(pl => pl.supplier_name).filter(Boolean))
  ).sort();

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="PO Number, Container No, or Stock Code... (Press Enter or click away)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={() => setAppliedSearchTerm(searchTerm)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setAppliedSearchTerm(searchTerm);
                  }
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Date Filter</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  label="Date Filter"
                >
                  <MenuItem value="">All Dates</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="next_week">Next Week</MenuItem>
                  <MenuItem value="next_two_weeks">Next Two Weeks</MenuItem>
                  <MenuItem value="next_month">Next Month</MenuItem>
                  <MenuItem value="next_two_months">Next Two Months</MenuItem>
                  <MenuItem value="next_three_months">Next Three Months</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  label="Supplier"
                >
                  <MenuItem value="">All Suppliers</MenuItem>
                  {uniqueSuppliers.map((supplier) => (
                    <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Merge Selected Button - always visible */}
            <Grid item xs={12} md={5}>
              <Tooltip
                title={selectedForMerge.size < 2 ? "Select 2 or more packing lists to merge into a group" : ""}
                placement="bottom"
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<MergeIcon />}
                    onClick={handleOpenMergeDialog}
                    disabled={selectedForMerge.size < 2}
                    sx={{
                      height: '56px',
                      backgroundColor: selectedForMerge.size >= 2 ? '#e91e63' : undefined,
                      '&:hover': {
                        backgroundColor: selectedForMerge.size >= 2 ? '#c2185b' : undefined
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(233, 30, 99, 0.12)',
                        color: 'rgba(233, 30, 99, 0.5)'
                      }
                    }}
                  >
                    {selectedForMerge.size >= 2
                      ? `Merge ${selectedForMerge.size} Selected`
                      : 'Merge Packing Lists'
                    }
                  </Button>
                </span>
              </Tooltip>
              {selectedForMerge.size === 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Select one more to merge
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Packing List Accordions */}
      {packingListGroups.length === 0 ? (
        <Box textAlign="center" py={8}>
          <ContainerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No packing lists uploaded
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload packing lists to see containers here
          </Typography>
        </Box>
      ) : (
        packingListGroups
          .sort((a, b) => {
            // Get ETA for each packing list (container delivery or expected delivery)
            const getETA = (pl: PackingListGroup) => {
              const earliestContainerDelivery = pl.containers
                .map(c => c.delivery_day)
                .filter(Boolean)
                .sort()[0];
              return earliestContainerDelivery || pl.expected_delivery;
            };

            const etaA = getETA(a);
            const etaB = getETA(b);

            // Sort by ETA ascending (earliest first), nulls last
            if (!etaA && !etaB) return 0;
            if (!etaA) return 1;
            if (!etaB) return -1;
            return new Date(etaA).getTime() - new Date(etaB).getTime();
          })
          .map((packingList) => (
          <Accordion
            key={packingList.import_id}
            expanded={expandedPacking === packingList.import_id}
            onChange={handleAccordionChange(packingList)}
            sx={{
              mb: 1,
              // Highlight merged batches with pink
              ...(packingList.is_merged && {
                borderLeft: '4px solid #e91e63',
                '& .MuiAccordionSummary-root': {
                  backgroundColor: 'rgba(233, 30, 99, 0.04)'
                }
              })
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%" gap={2}>
                {/* Checkbox for merge selection - only for non-merged batches */}
                {!packingList.is_merged && (
                  <Checkbox
                    checked={selectedForMerge.has(packingList.import_id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleMergeCheckboxChange(packingList.import_id, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{
                      p: 0,
                      mr: 0,
                      color: '#e91e63',
                      '&.Mui-checked': { color: '#e91e63' }
                    }}
                  />
                )}

                {/* Merged badge */}
                {packingList.is_merged && (
                  <Tooltip title={`Merged: ${packingList.filenames?.join(', ')}`}>
                    <Chip
                      icon={<FolderIcon />}
                      label={`${packingList.filenames?.length || 0} files`}
                      size="small"
                      sx={{
                        backgroundColor: '#e91e63',
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                  </Tooltip>
                )}

                <Chip
                  label={packingList.supplier_name}
                  size="small"
                  variant="outlined"
                />

                {/* Show all unique POs from containers */}
                {Array.from(new Set(
                  packingList.containers.flatMap(c => c.po_numbers || [c.primary_po_number]).filter(Boolean)
                )).map((poNumber) => (
                  <Chip
                    key={poNumber}
                    label={`PO ${poNumber}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigateToPO && onNavigateToPO(poNumber as string);
                    }}
                  />
                ))}

                <Typography variant="h6" sx={{ minWidth: 200 }}>
                  {packingList.batch_description || packingList.filename}
                </Typography>

                {/* ETA - from PO expected delivery or earliest container delivery */}
                {(() => {
                  // First try container scheduled delivery dates
                  const earliestContainerDelivery = packingList.containers
                    .map(c => c.delivery_day)
                    .filter(Boolean)
                    .sort()[0];

                  // Use container delivery if scheduled, otherwise use PO expected delivery
                  const etaDate = earliestContainerDelivery || packingList.expected_delivery;

                  return etaDate ? (
                    <Chip
                      label={`ETA: ${new Date(etaDate).toLocaleDateString('en-AU', {
                        weekday: 'short',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}`}
                      size="small"
                      sx={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        fontWeight: 500,
                        ml: 'auto'
                      }}
                    />
                  ) : null;
                })()}

                <Chip
                  label={packingList.containers.length}
                  size="small"
                  icon={<ContainerIcon />}
                />

                {/* Status indicator */}
                {(() => {
                  const receivedCount = packingList.containers.filter(c => isContainerReceived(c.status)).length;
                  const totalCount = packingList.containers.length;
                  const allReceived = receivedCount === totalCount && totalCount > 0;

                  return allReceived ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="All Received"
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip
                      label={`${receivedCount}/${totalCount} received`}
                      size="small"
                      color={receivedCount > 0 ? "warning" : "default"}
                      variant="outlined"
                    />
                  );
                })()}

                {/* Unmerge button - only for merged batches */}
                {packingList.is_merged && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UnmergeIcon />}
                    onClick={(e) => handleUnmerge(packingList, e)}
                    disabled={unmergeLoading}
                    sx={{
                      mr: 1,
                      color: '#e91e63',
                      borderColor: '#e91e63',
                      '&:hover': {
                        borderColor: '#c2185b',
                        backgroundColor: 'rgba(233, 30, 99, 0.04)'
                      }
                    }}
                  >
                    Unmerge
                  </Button>
                )}

                {/* Apply Harders Sequence — scoped to the packing list's PO */}
                {packingList.po_number && packingList.containers.length > 0 && (
                  <Tooltip title={`Paste the Harders call-up list for PO ${packingList.po_number}`}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FormatListNumberedIcon />}
                      onClick={(e) => openHardersModal(packingList, e)}
                    >
                      Harders Order
                    </Button>
                  </Tooltip>
                )}

                {/* Copy Delivery Plan — Harders-style text round-trip */}
                {packingList.containers.length > 0 && (
                  <Tooltip title="Copy delivery plan in Harders-style format for email">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={(e) => handleCopyDeliveryPlan(packingList, e)}
                    >
                      Copy Plan
                    </Button>
                  </Tooltip>
                )}

                {/* Delete packing list button - only for non-merged batches */}
                {!packingList.is_merged && (
                  <Tooltip title="Delete this packing list and associated containers">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(packingList, e)}
                      disabled={deleteLoading}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'white'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Box>
                {/* Show nested file list for merged batches */}
                {packingList.is_merged && packingList.filenames && packingList.filenames.length > 1 && (
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'rgba(233, 30, 99, 0.04)', borderColor: 'rgba(233, 30, 99, 0.3)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#e91e63' }} gutterBottom>
                      Merged Files ({packingList.filenames.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {packingList.filenames.map((filename, idx) => (
                        <Chip
                          key={idx}
                          icon={<FileIcon />}
                          label={filename}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: 'rgba(233, 30, 99, 0.5)' }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                {(() => {
                  // Stock code finder: which containers carry each SKU in this shipment
                  const focus = stockFocus[packingList.import_id] || null;
                  const stockIndex = new Map<string, { containerIds: Set<number>, lines: ContainerLine[] }>();
                  packingList.containers.forEach(c => {
                    (containerLines[c.id] || []).forEach(line => {
                      if (!line.stock_code) return;
                      const entry = stockIndex.get(line.stock_code) || { containerIds: new Set<number>(), lines: [] };
                      entry.containerIds.add(c.id);
                      entry.lines.push(line);
                      stockIndex.set(line.stock_code, entry);
                    });
                  });
                  const stockCodes = Array.from(stockIndex.keys()).sort();
                  const focusEntry = focus ? stockIndex.get(focus) : undefined;
                  const visibleContainers = focusEntry
                    ? packingList.containers.filter(c => focusEntry.containerIds.has(c.id))
                    : packingList.containers;
                  const allExpanded = visibleContainers.length > 0
                    && visibleContainers.every(c => expandedContainers.includes(c.id));

                  return (
                    <>
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={stockCodes.length > 1 ? 1 : 0}>
                          <Typography variant="subtitle2">
                            Stock codes in this shipment
                          </Typography>
                          {loadingShipmentLines[packingList.import_id] && <CircularProgress size={16} />}
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ ml: 'auto' }}
                            onClick={() => setAllContainersExpanded(packingList, !allExpanded)}
                            endIcon={<ExpandMoreIcon sx={{ transform: allExpanded ? 'rotate(180deg)' : 'none' }} />}
                          >
                            {allExpanded ? 'Hide All' : 'Show All'}
                          </Button>
                        </Box>
                        {stockCodes.length > 1 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {stockCodes.map(code => {
                              const entry = stockIndex.get(code)!;
                              const selected = focus === code;
                              return (
                                <Tooltip key={code} title={`${describeStockQty(entry.lines)} across ${entry.containerIds.size} container${entry.containerIds.size === 1 ? '' : 's'}`}>
                                  <Chip
                                    label={`${code} · ${entry.containerIds.size}`}
                                    size="small"
                                    color={selected ? 'primary' : 'default'}
                                    variant={selected ? 'filled' : 'outlined'}
                                    onClick={() => setStockFocus(prev => ({
                                      ...prev,
                                      [packingList.import_id]: selected ? null : code
                                    }))}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}
                        {focusEntry && (
                          <Alert
                            severity="info"
                            sx={{ mt: 1, py: 0 }}
                            action={
                              <Button
                                size="small"
                                color="inherit"
                                onClick={() => setStockFocus(prev => ({ ...prev, [packingList.import_id]: null }))}
                              >
                                Show all containers
                              </Button>
                            }
                          >
                            {focusEntry.containerIds.size} of {packingList.containers.length} containers carry <strong>{focus}</strong> ({describeStockQty(focusEntry.lines)})
                          </Alert>
                        )}
                      </Paper>

                      {visibleContainers.map((container) => (
                  <ContainerCard
                    key={container.id}
                    highlightStock={focusEntry ? focus : null}
                    container={container}
                    onPriorityToggle={handlePriorityToggle}
                    getDockDisplayName={getDockDisplayName}
                    getStatusColor={getStatusColor}
                    isExpanded={expandedContainers.includes(container.id)}
                    onExpand={handleContainerExpand}
                    containerLines={containerLines[container.id] || []}
                    onUpdateContainer={handleUpdateContainer}
                    onLocalUpdate={applyContainerLocalUpdate}
                    dockOptions={dockOptions}
                    onViewReport={handleViewContainerReport}
                  />
                      ))}
                    </>
                  );
                })()}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Container Detail Modal */}
      <ContainerDetailModal
        open={detailModalOpen}
        container={selectedContainer}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedContainer(null);
        }}
        onSaved={() => {
          // Reload packing list groups to reflect changes
          loadPackingListGroups();
        }}
      />

      {/* Delete Packing List Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Packing List
        </DialogTitle>
        <DialogContent>
          {packingListToDelete && deletePreview && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this packing list?
              </Typography>

              <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.100' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  File: <strong>{packingListToDelete.filename}</strong>
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Supplier: <strong>{packingListToDelete.supplier_name}</strong>
                </Typography>
                {deletePreview.po_number && (
                  <Typography variant="subtitle2" color="text.secondary">
                    PO: <strong>{deletePreview.po_number}</strong>
                  </Typography>
                )}
              </Paper>

              <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                This will delete:
              </Typography>
              <Box component="ul" sx={{ mt: 1 }}>
                {deletePreview.containers_to_delete > 0 && (
                  <li>
                    <Typography variant="body2">
                      {deletePreview.containers_to_delete} container(s): {deletePreview.container_list?.join(', ')}
                    </Typography>
                  </li>
                )}
                {deletePreview.containers_to_partially_update > 0 && (
                  <li>
                    <Typography variant="body2">
                      Lines from {deletePreview.containers_to_partially_update} shared container(s)
                      {deletePreview.partial_container_list?.map((p: any) => (
                        <Typography key={p.container_no} variant="caption" display="block" sx={{ ml: 2 }}>
                          • {p.container_no}: {p.message}
                        </Typography>
                      ))}
                    </Typography>
                  </li>
                )}
                <li>
                  <Typography variant="body2">
                    {deletePreview.packing_lines_to_delete} packing list line(s)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    {deletePreview.container_lines_to_delete} container line(s)
                  </Typography>
                </li>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                This action cannot be undone. You will need to re-upload the packing list to restore this data.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Container Report Modal */}
      <DailyContainerReport
        open={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setReportData(null);
        }}
        reportDate={reportData?.container?.delivery_day || new Date().toISOString().split('T')[0]}
        containers={reportData ? [reportData] : []}
      />

      {/* Merge Packing Lists Dialog */}
      <MergeDialog
        open={mergeDialogOpen}
        onClose={handleCloseMergeDialog}
        onConfirm={handleMergeConfirm}
        selectedPackingLists={selectedPackingLists}
      />

      {/* Apply Harders Sequence Dialog */}
      <Dialog open={hardersModalOpen} onClose={closeHardersModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Apply Harders call-up order
          {hardersPackingList?.po_number && (
            <Typography variant="body2" color="text.secondary">
              PO {hardersPackingList.po_number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste the Harders list. Containers are reordered to match the sequence.
            Extra text on each line (dates, times, notes) is ignored.
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={8}
              placeholder={'UETU8485337   Thu 9/4 6 AM\nTCKU7071706   Thu 9/4 7.30 AM\nTRHU5099600   Thu 9/4 9 AM'}
              value={hardersText}
              onChange={(e) => setHardersText(e.target.value)}
              variant="outlined"
              helperText={`${hardersText.split('\n').filter(l => l.trim()).length} non-blank lines`}
            />

            {hardersResult && (
              hardersResult.success ? (
                <Alert severity={hardersResult.not_found_count || hardersResult.unparseable_count ? 'warning' : 'success'}>
                  <Typography variant="body2" fontWeight="bold">
                    {hardersResult.updated_count} container(s) reordered.
                  </Typography>
                  {hardersResult.not_found_count > 0 && (
                    <>
                      <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600 }}>
                        {hardersResult.not_found_count} not found on PO {hardersResult.po_number}:
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                        {hardersResult.not_found.map((nf: any) => (
                          <li key={`nf-${nf.line_number}`}>
                            <Typography variant="caption">
                              Line {nf.line_number}: {nf.container_no}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                  {hardersResult.unparseable_count > 0 && (
                    <>
                      <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600 }}>
                        {hardersResult.unparseable_count} unparseable line(s) (no container number detected):
                      </Typography>
                      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                        {hardersResult.unparseable.map((u: any) => (
                          <li key={`up-${u.line_number}`}>
                            <Typography variant="caption">
                              Line {u.line_number}: "{u.text}"
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </Alert>
              ) : (
                <Alert severity="error">
                  {hardersResult.error || 'Failed to apply sequence'}
                </Alert>
              )
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHardersModal}>Close</Button>
          <Button
            variant="contained"
            onClick={handleApplyHardersSequence}
            disabled={!hardersText.trim() || hardersSubmitting}
            startIcon={hardersSubmitting ? <CircularProgress size={16} /> : <FormatListNumberedIcon />}
          >
            {hardersSubmitting ? 'Applying...' : 'Apply sequence'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy feedback snackbar */}
      <Snackbar
        open={copyFeedback !== null}
        autoHideDuration={4000}
        onClose={() => setCopyFeedback(null)}
        message={copyFeedback}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// Separate MergeDialog component to isolate state and prevent parent re-renders on typing
interface MergeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (description: string) => Promise<void>;
  selectedPackingLists: PackingListGroup[];
}

const MergeDialog: React.FC<MergeDialogProps> = React.memo(({
  open,
  onClose,
  onConfirm,
  selectedPackingLists
}) => {
  // Local state for description - typing here won't re-render parent
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Generate default batch name when dialog opens
  useEffect(() => {
    if (open && selectedPackingLists.length > 0) {
      // Get month from expected delivery date (use earliest if multiple)
      const deliveryDates = selectedPackingLists
        .map(pl => pl.expected_delivery)
        .filter(Boolean)
        .sort();

      let monthName = '';
      if (deliveryDates.length > 0) {
        const date = new Date(deliveryDates[0] as string);
        monthName = date.toLocaleString('en-US', { month: 'long' });
      }

      // Get unique PO numbers from containers
      const poNumbers = Array.from(new Set(
        selectedPackingLists.flatMap(pl =>
          pl.containers.flatMap(c => c.po_numbers || [c.primary_po_number]).filter(Boolean)
        )
      )).sort();
      const poString = poNumbers.length > 0
        ? poNumbers.length === 1
          ? `PO${poNumbers[0]}`
          : `PO${poNumbers[0]}+${poNumbers.length - 1}`
        : '';

      // Get supplier code (use first non-empty, they should all be same for typical merge)
      const supplier = selectedPackingLists.find(pl => pl.supplier_name)?.supplier_name || '';

      // Build default name: "January PO19748 STORAUS Shipment"
      const parts = [monthName, poString, supplier, 'Shipment'].filter(Boolean);
      setDescription(parts.join(' '));
    } else if (!open) {
      setDescription('');
    }
  }, [open, selectedPackingLists]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(description);
    } catch (err) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeIcon sx={{ color: '#e91e63' }} />
        Merge Packing Lists
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Merging will group these packing lists together. Containers with the same container number
          will appear once with combined lines from all files.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, my: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Files to merge ({selectedPackingLists.length})
          </Typography>
          <List dense>
            {selectedPackingLists.map((pl, idx) => (
              <ListItem key={pl.import_id} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <FileIcon fontSize="small" sx={{ color: idx === 0 ? '#e91e63' : 'action.active' }} />
                </ListItemIcon>
                <ListItemText
                  primary={pl.filename}
                  secondary={`${pl.containers.length} container(s) • ${pl.supplier_name || 'Unknown supplier'}`}
                  primaryTypographyProps={{
                    fontWeight: idx === 0 ? 'bold' : 'normal'
                  }}
                />
                {idx === 0 && (
                  <Chip
                    label="Primary"
                    size="small"
                    variant="outlined"
                    sx={{ color: '#e91e63', borderColor: '#e91e63' }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Paper>

        <TextField
          fullWidth
          label="Batch Name (optional)"
          placeholder="e.g., February Klabin Shipment"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText="Give this merged batch a friendly name for easy identification"
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || selectedPackingLists.length < 2}
          startIcon={loading ? <CircularProgress size={16} /> : <MergeIcon />}
          sx={{
            backgroundColor: '#e91e63',
            '&:hover': { backgroundColor: '#c2185b' }
          }}
        >
          {loading ? 'Merging...' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

// Container Card Component
interface ContainerCardProps {
  container: Container;
  onPriorityToggle: (container: Container) => void;
  getDockDisplayName: (dock: string) => string;
  getStatusColor: (status?: string) => 'default' | 'primary' | 'secondary' | 'success';
  isExpanded: boolean;
  onExpand: (containerId: number) => void;
  containerLines: ContainerLine[];
  onUpdateContainer: (containerId: number, field: string, value: string) => void;
  onLocalUpdate: (containerId: number, field: string, value: string) => void;
  dockOptions: Array<{value: string, label: string}>;
  onViewReport: (container: Container) => void;
  highlightStock?: string | null;
}

const ContainerCard: React.FC<ContainerCardProps> = ({
  container,
  onPriorityToggle,
  getDockDisplayName,
  getStatusColor,
  isExpanded,
  onExpand,
  containerLines,
  onUpdateContainer,
  onLocalUpdate,
  dockOptions,
  onViewReport,
  highlightStock
}) => {
  // Local state for inline editing (save on blur)
  const [localDeliveryDay, setLocalDeliveryDay] = React.useState(container.delivery_day || '');
  const [localDeliveryTime, setLocalDeliveryTime] = React.useState(container.delivery_time || '');
  const [localSealNo, setLocalSealNo] = React.useState(container.seal_no || '');
  const [editingContainerNo, setEditingContainerNo] = React.useState(false);
  const [containerNoDraft, setContainerNoDraft] = React.useState(container.container_no);
  const [containerNoError, setContainerNoError] = React.useState<string | null>(null);
  const [savingContainerNo, setSavingContainerNo] = React.useState(false);

  // Update local state when container prop changes
  React.useEffect(() => {
    setLocalDeliveryDay(container.delivery_day || '');
    setLocalDeliveryTime(container.delivery_time || '');
    setLocalSealNo(container.seal_no || '');
    setContainerNoDraft(container.container_no);
  }, [container.delivery_day, container.delivery_time, container.seal_no, container.container_no]);

  const commitContainerNo = async () => {
    const trimmed = containerNoDraft.trim();
    if (!trimmed || trimmed === container.container_no) {
      setContainerNoDraft(container.container_no);
      setEditingContainerNo(false);
      setContainerNoError(null);
      return;
    }
    try {
      setSavingContainerNo(true);
      setContainerNoError(null);
      await api.put(`/containers/${container.id}`, { container_no: trimmed });
      onLocalUpdate(container.id, 'container_no', trimmed);
      setEditingContainerNo(false);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to update container number';
      setContainerNoError(msg);
      setContainerNoDraft(container.container_no);
    } finally {
      setSavingContainerNo(false);
    }
  };

  const cancelEditContainerNo = () => {
    setContainerNoDraft(container.container_no);
    setEditingContainerNo(false);
    setContainerNoError(null);
  };

  return (
    <Card
      sx={{
        mb: 1,
        border: container.is_priority ? '2px solid #f57c00' : '1px solid #e0e0e0',
        backgroundColor: container.is_priority ? '#fff8e1' : 'white'
      }}
      id={`container-${container.container_no}`}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1}>
          {/* Single Row: Everything inline */}
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton size="small" onClick={() => onPriorityToggle(container)}>
              {container.is_priority ? (
                <Tooltip title={container.priority_comment}>
                  <StarIcon color="warning" />
                </Tooltip>
              ) : (
                <StarBorderIcon />
              )}
            </IconButton>

            {container.delivery_sequence != null && (
              <Tooltip title="Position in uploaded Harders call-up sequence">
                <Chip
                  label={`#${container.delivery_sequence}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                />
              </Tooltip>
            )}

            {editingContainerNo ? (
              <TextField
                size="small"
                value={containerNoDraft}
                onChange={(e) => setContainerNoDraft(e.target.value)}
                onBlur={commitContainerNo}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur();
                  } else if (e.key === 'Escape') {
                    cancelEditContainerNo();
                  }
                }}
                disabled={savingContainerNo}
                autoFocus
                error={!!containerNoError}
                helperText={containerNoError || undefined}
                inputProps={{
                  style: {
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    padding: '4px 8px',
                  },
                }}
                sx={{ minWidth: 220 }}
              />
            ) : (
              <Tooltip title={containerNoError || 'Click to edit container number'}>
                <Typography
                  variant="h6"
                  onClick={() => setEditingContainerNo(true)}
                  sx={{
                    minWidth: '120px',
                    cursor: 'pointer',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    color: containerNoError ? 'error.main' : 'inherit',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {container.container_no}
                </Typography>
              </Tooltip>
            )}

            <Chip
              label={`${container.line_count} lines`}
              size="small"
              variant="outlined"
            />

            {highlightStock && (
              <Chip
                label={`${highlightStock}: ${describeStockQty(containerLines.filter(l => l.stock_code === highlightStock))}`}
                size="small"
                color="primary"
              />
            )}

            {container.is_shared && (
              <Tooltip title={`Contains stock from ${container.po_numbers?.length || 0} different POs`}>
                <Chip
                  icon={<CallSplitIcon />}
                  label="Multi-PO"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}

            <Chip
              icon={isContainerReceived(container.status) ? <CheckCircleIcon /> : undefined}
              label={isContainerReceived(container.status) ? 'Received - View Report' : 'In Transit'}
              size="small"
              color={getStatusColor(container.status)}
              variant={isContainerReceived(container.status) ? 'filled' : 'outlined'}
              onClick={isContainerReceived(container.status) ? () => onViewReport(container) : undefined}
              sx={isContainerReceived(container.status) ? { cursor: 'pointer' } : undefined}
            />

            <TextField
              type="date"
              size="small"
              value={localDeliveryDay}
              onChange={(e) => setLocalDeliveryDay(e.target.value)}
              onBlur={(e) => {
                if (e.target.value !== container.delivery_day) {
                  onUpdateContainer(container.id, 'delivery_day', e.target.value);
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
              placeholder="Date"
            />

            <TextField
              size="small"
              value={localDeliveryTime ? localDeliveryTime.substring(0, 5) : ''}
              onChange={(e) => {
                // Allow typing in HH:MM format
                const val = e.target.value;
                // Auto-insert colon after 2 digits
                if (val.length === 2 && !val.includes(':') && localDeliveryTime.length < 2) {
                  setLocalDeliveryTime(val + ':');
                } else {
                  setLocalDeliveryTime(val);
                }
              }}
              onBlur={(e) => {
                const val = e.target.value;
                // Validate HH:MM format (24-hour)
                const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
                if (timeRegex.test(val)) {
                  // Normalize to HH:MM:00 format for backend
                  const normalized = val.padStart(5, '0') + ':00';
                  if (normalized !== container.delivery_time) {
                    onUpdateContainer(container.id, 'delivery_time', normalized);
                  }
                } else if (val === '') {
                  if (container.delivery_time) {
                    onUpdateContainer(container.id, 'delivery_time', '');
                  }
                }
              }}
              placeholder="HH:MM"
              inputProps={{ maxLength: 5 }}
              sx={{ width: 100 }}
            />

            <TextField
              size="small"
              value={localSealNo}
              onChange={(e) => setLocalSealNo(e.target.value)}
              onBlur={(e) => {
                if (e.target.value !== container.seal_no) {
                  onUpdateContainer(container.id, 'seal_no', e.target.value);
                }
              }}
              placeholder="Seal #"
              sx={{ width: 180 }}
            />

            <FormControl size="small" sx={{ width: 150 }}>
              <Select
                value={container.dock || ''}
                onChange={(e) => onUpdateContainer(container.id, 'dock', e.target.value)}
                displayEmpty
                sx={{ '& .MuiOutlinedInput-input': { py: '16.5px' } }}
              >
                <MenuItem value="">
                  <em>No Dock</em>
                </MenuItem>
                {dockOptions.map((dock) => (
                  <MenuItem key={dock.value} value={dock.value}>
                    {dock.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              size="small"
              onClick={() => onExpand(container.id)}
              endIcon={<ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
              sx={{ ml: 'auto' }}
            >
              {isExpanded ? 'Hide' : 'Details'}
            </Button>
          </Box>

          {/* Priority Comment - Only if priority */}
          {container.is_priority && (
            <Box>
              <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                Priority: "{container.priority_comment}"
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      <Collapse in={isExpanded}>
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="h6" gutterBottom>
            Stock Codes & Quantities
          </Typography>
          {containerLines.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Stock Code</strong></TableCell>
                    <TableCell><strong>PO/s</strong></TableCell>
                    <TableCell align="right"><strong>Quantity</strong></TableCell>
                    <TableCell align="right"><strong>Count</strong></TableCell>
                    <TableCell align="right"><strong>Weight (Tonnes)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const grouped = containerLines.reduce((acc: Record<string, { totalSheets: number, totalMetres: number, palletCount: number, reelCount: number, totalWeight: number, reelWeightKg: number, poNumbers: Set<string> }>, line) => {
                      if (!acc[line.stock_code]) {
                        acc[line.stock_code] = { totalSheets: 0, totalMetres: 0, palletCount: 0, reelCount: 0, totalWeight: 0, reelWeightKg: 0, poNumbers: new Set() };
                      }

                      // Track PO numbers
                      if (line.po_number) {
                        acc[line.stock_code].poNumbers.add(line.po_number);
                      }

                      // Each line is either a pallet or a reel
                      const isReel = line.sheets === 0 && (line.rolls_per_pallet > 0 || line.metres > 0);

                      if (isReel) {
                        acc[line.stock_code].reelCount += 1;
                        acc[line.stock_code].totalMetres += line.metres || 0;
                        acc[line.stock_code].reelWeightKg += (line.net_weight_tonnes || 0) * 1000;
                      } else {
                        acc[line.stock_code].palletCount += 1;
                        acc[line.stock_code].totalSheets += line.sheets || 0;
                      }

                      acc[line.stock_code].totalWeight += line.net_weight_tonnes || 0;
                      return acc;
                    }, {});

                    const groupedEntries = Object.entries(grouped);

                    const containerTotals = groupedEntries.reduce((totals, [_, data]) => ({
                      totalSheets: totals.totalSheets + data.totalSheets,
                      totalMetres: totals.totalMetres + data.totalMetres,
                      totalPallets: totals.totalPallets + data.palletCount,
                      totalReels: totals.totalReels + data.reelCount,
                      totalWeight: totals.totalWeight + data.totalWeight,
                      totalReelWeightKg: totals.totalReelWeightKg + data.reelWeightKg
                    }), { totalSheets: 0, totalMetres: 0, totalPallets: 0, totalReels: 0, totalWeight: 0, totalReelWeightKg: 0 });

                    return (
                      <>
                        {groupedEntries.map(([stockCode, data]) => {
                          const countLabel = data.palletCount > 0 && data.reelCount > 0
                            ? `${data.palletCount} pallets, ${data.reelCount} reels`
                            : data.reelCount > 0
                              ? `${data.reelCount} reels`
                              : `${data.palletCount} pallets`;

                          const poList = Array.from(data.poNumbers).sort().join(', ');

                          return (
                            <TableRow
                              key={stockCode}
                              sx={stockCode === highlightStock ? { backgroundColor: 'rgba(25, 118, 210, 0.08)', '& td': { fontWeight: 600 } } : undefined}
                            >
                              <TableCell>{stockCode}</TableCell>
                              <TableCell>{poList || '-'}</TableCell>
                              <TableCell align="right">
                                {data.totalSheets > 0
                                  ? `${data.totalSheets.toLocaleString()} sheets`
                                  : data.reelWeightKg > 0
                                    ? `${data.reelWeightKg.toLocaleString()} kg`
                                    : '-'
                                }
                              </TableCell>
                              <TableCell align="right">{countLabel}</TableCell>
                              <TableCell align="right">{data.totalWeight.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {groupedEntries.length > 1 && (
                          <TableRow sx={{ backgroundColor: '#f5f5f5', borderTop: '2px solid #ddd' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                            <TableCell></TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {containerTotals.totalSheets > 0 && containerTotals.totalReelWeightKg > 0
                                ? `${containerTotals.totalSheets.toLocaleString()} sheets / ${containerTotals.totalReelWeightKg.toLocaleString()} kg`
                                : containerTotals.totalSheets > 0
                                  ? `${containerTotals.totalSheets.toLocaleString()} sheets`
                                  : containerTotals.totalReelWeightKg > 0
                                    ? `${containerTotals.totalReelWeightKg.toLocaleString()} kg`
                                    : '-'
                              }
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {containerTotals.totalPallets > 0 && containerTotals.totalReels > 0
                                ? `${containerTotals.totalPallets} pallets, ${containerTotals.totalReels} reels`
                                : containerTotals.totalReels > 0
                                  ? `${containerTotals.totalReels} reels`
                                  : `${containerTotals.totalPallets} pallets`
                              }
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {containerTotals.totalWeight.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Loading container details...
            </Typography>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ContainerDashboard;
